// eslint-disable-next-line no-unused-vars
import React, { useMemo } from 'react';
import { string, bool, func, object, number } from 'prop-types';
import styled from '@emotion/styled';
import { Loading, Button, TextInputSkeleton } from '@carbon/react';
import { Tooltip as ReactTooltip } from 'react-tooltip';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';

import useNumberUtils from 'Hooks/useNumberUtils';
import usePermissions from 'Hooks/usePermissions';
import { COUNTRIES, PERMISSION } from 'Constants';

const StyledContainer = styled.div`
  top: 140px;
  position: sticky;
  padding: 15px;
  border: 1px solid ${ props => props.theme.products.colors.border };
  background-color: ${ props => props.theme.products.colors.addToFavorite };

  .title {
    font-size: 1.25rem;
    font-weight: 600;
    margin-bottom: 10px;
    color: #464545;
  }

  .react-tooltip {
    max-width: 278px;
    text-align: center;
  }

  button.cds--btn {
    min-width: 0;
    width: 100%;
    height: 50px;
    padding: 0;
    margin: 10px 0;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 1.125rem;
    border-color: inherit;

    &.submit {
      background-color: ${ props => props.theme.colors.default };

      .cds--loading {
        left: 25px;
        width: 18px;
        height: 18px;
      }

      svg {
        height: 18px;
      }
    }

    &.submit:not(:disabled) {
      color: ${ props => props.theme.colors.white };
    }

    &.reserve {

      svg {
        height: 18px;
      }

      &:not(:disabled) {
        color: ${ props => props.theme.colors.blue };
        border: 1px solid ${ props => props.theme.colors.blue };
      }
    }
  }

  .item {
    width: 100%;
    display: grid;
    grid-template-columns: auto 95px;
    font-size: 0.875rem;
    line-height: 1.15rem;
    color: ${ props => props.theme.colors.darkGray };
    margin: 5px 0;

    p {
      font-size: inherit;
      line-height: inherit;
      text-align: left;
    }

    p + span {
      text-align: right;
      text-transform: capitalize;
    }

    &.total {
      font-size: 1.5rem;
      line-height: 1.65rem;
      font-weight: 600;
      grid-template-columns: auto auto;

      p {
        font-size: inherit;
        font-weight: inherit;
      }

      .cds--skeleton {
        height: 1.6rem;
        width: 100%;
      }

      &.totalWithVat {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        font-size: 1.25rem;
        line-height: 1.3rem;

        span {
          margin-top: 15px;
        }
      }

      span {
        font-size: 1.625rem;
        line-height: 2rem;
      }
    }

    .cds--form-item {
      max-width: 60px;
    }

    .cds--skeleton {
      height: 1rem;
      width: 60px;
    }

    + hr {
      color: ${ props => props.theme.products.colors.border };
    }
  }

  @media screen and (max-width: ${ props => props.theme.device.mobile }) {
    position: static;
    min-width: 100% !important;
    max-width: none !important;
    background: #F5F7F9 !important;
    border: 1px solid #E2E2E2 !important;

    .item {
      p {
        font-size: 14px !important;
        text-align: left !important;

        span {
          font-size: 18px;
          font-weight: 600;
        }
      }

      &.total {
        p {
          font-size: 14px;
          font-weight: 600;

          span {
            font-size: 18px;
            font-weight: 600;
          }
        }
      }
    }
  
    .title {
      font-size: 20px;
      padding-bottom: 0;
      font-weight: 600;
      display: inline-block !important;
    }

    hr {
      display: block !important;
      border: 1px solid #E2E2E2;
    }

    button.cds--btn {
      max-width: 100%;
    }
  }
`;

const OrderSummary = props => {
  const {
    canPlaceOrder,
    canPlaceStockReservationOrder,
    canShowButtons,
    canReserve,
    orderType,
    loading,
    defaultCurrency,
    isDropShipping,
    dsCurrency,
    onSubmit,
    onReserve,
    totals,
    displayVat,
    selectedDSCountry,
    hasSupplierProducts,
    productsType,
  } = props;
  const { t } = useTranslation();
  const theme = useTheme();
  const { formatNumber } = useNumberUtils();
  const { can } = usePermissions();
  const canAddToCart = can(PERMISSION.ADD_TO_CART);
  const tooltipContent = (() => {
    if (productsType === 'mixed-products') {
      return t('order.unableToReserveSupplierResealed');
    }
    if (productsType === 'supplier-products') {
      return t('order.unableToReserveSupplier');
    }
    return t('order.unableToReserveResealed');
  })();

  /* eslint-disable no-restricted-globals */
  const memoSummaryNoDs = useMemo(() => (
    <div className="wrapper">
      <p className="title">{ t('order.summary') }</p>
      <div className="item price">
        <p>
          { displayVat ? t('order.priceNoVat') : t('order.price') }:
        </p>
        { isNaN(totals.price)
          ? <TextInputSkeleton hideLabel />
          : (<span>{ formatNumber(totals.price, { currency: defaultCurrency }) }</span>)
        }
      </div>
      {
        displayVat && (
          <div className="item tva">
            <p>{ t('order.vat') }:</p>
            { isNaN(totals.vat)
              ? <TextInputSkeleton hideLabel />
              : (<span>{ formatNumber(totals.vat, { currency: defaultCurrency }) }</span>)
            }
          </div>
        )
      }
      {/*<div className="item transport">*/ }
      {/*  <p>{ t('order.transportPrice') }:</p>*/ }
      {/*  { isNaN(totals.transportValue)*/ }
      {/*    ? <TextInputSkeleton hideLabel />*/ }
      {/*    : (<span>{ formatNumber(0, { currency }) }</span>)*/ }
      {/*  }*/ }
      {/*<span>{ formatNumber(0, { currency }) }</span>*/ }
      {/*</div>*/ }
      <hr />
      <div className="item total">
        <p>
          { t('order.total') }:
        </p>
        { isNaN(totals.price)
          ? <TextInputSkeleton hideLabel />
          : (<span>{ formatNumber(totals.total, { currency: defaultCurrency }) }</span>)
        }
      </div>
      { canShowButtons && (
        <>
          <Button
            onClick={ onSubmit }
            data-test="onSubmitOrder"
            className="submit red"
            kind="danger"
            disabled={ loading || !canPlaceOrder || isNaN(totals.price) || !canAddToCart }
          >
            { (loading && orderType !== 'reservation') && (
              <Loading active={ loading } small withOverlay={ false } />
            ) }
            { t('order.placeOrder') }
          </Button>
          { (canPlaceStockReservationOrder) && (
            <>
              <Button
                kind="ghost"
                className="reserve blue"
                data-test="onReserveOrder"
                onClick={ onReserve }
                disabled={ loading || !canReserve || !canPlaceOrder || isNaN(totals.price) || hasSupplierProducts }
                id={ (!canReserve || hasSupplierProducts) ? 'reserveDisabled' : 'hook-reserve-button' }
              >
                { (loading && orderType === 'reservation') && (
                  <Loading active={ loading } small withOverlay={ false } />
                ) }
                { t('order.reserveOrder') }
              </Button>
              <ReactTooltip
                className="reserveDisabled"
                anchorId="reserveDisabled"
                variant="light"
                offset={ 10 }
                place="bottom-end"
                content={ (
                  <p>{ tooltipContent }</p>
                ) }
              />
            </>
          ) }
        </>
      ) }
    </div>
  ), [
    totals,
    onSubmit,
    canShowButtons,
    defaultCurrency,
    canReserve,
    loading,
    canPlaceOrder,
    displayVat
  ]);

  const memoSummaryDs = useMemo(() => (
    <div className="wrapper">
      <p className="title">{ t('order.dsSummary') }</p>
      <div className="item price">
        <p>
          { t('order.priceNoVat') }:
        </p>
        { isNaN(totals.price)
          ? <TextInputSkeleton hideLabel />
          : (<span>{ formatNumber(totals.price, { currency: defaultCurrency }) }</span>)
        }
      </div>
      <div className="item tva">
        <p>
          { t('order.vat') }:
        </p>
        { isNaN(totals.vat)
          ? <TextInputSkeleton hideLabel />
          : (<span>{ formatNumber(totals.vat, { currency: defaultCurrency }) }</span>)
        }
      </div>
      <div className="item totalWithVat">
        <p>
          { t('order.totalWithVat') }:
        </p>
        { isNaN(totals.total)
          ? <TextInputSkeleton hideLabel />
          : <span>{ formatNumber(totals.total, { currency: defaultCurrency }) }</span>
        }
      </div>
      <div className="item summaryTransportValue">
        <p>
          { t('order.extraTax') }:
        </p>
        <span>{ formatNumber(totals.transportValue || 0, { currency: selectedDSCountry ? COUNTRIES[selectedDSCountry]?.currency : (dsCurrency || defaultCurrency) }) }</span>
      </div>
      <hr />
      <div className="item total totalWithVat">
        <p>
          { t('order.totalWithVatForClient') }:
        </p>
        { isNaN(totals.dsTotal)
          ? <TextInputSkeleton hideLabel />
          : <span>{ formatNumber(totals.dsTotal, { currency: selectedDSCountry ? COUNTRIES[selectedDSCountry]?.currency : (dsCurrency || defaultCurrency) }) }</span>
        }
      </div>
      {
        canShowButtons && (
          <Button
            data-test="onSubmitOrder"
            onClick={ onSubmit }
            className="submit"
            kind="danger"
            disabled={ loading || !canPlaceOrder || isNaN(totals.total) || !canAddToCart }
            type="submit"
          >
            { loading && <Loading active={ loading } small withOverlay={ false } /> }
            { t('order.placeOrder') }
          </Button>
        )
      }
    </div>
  ), [
    totals,
    onSubmit,
    canShowButtons,
    defaultCurrency,
    loading,
    canPlaceOrder,
    displayVat
  ]);
  /* eslint-enable no-restricted-globals */
  return (
    <StyledContainer theme={ theme } className="orderSummary">
      { isDropShipping ? memoSummaryDs : memoSummaryNoDs }
    </StyledContainer>
  );
};
OrderSummary.propTypes = {
  isDropShipping: bool,
  dsCurrency: string,
  canShowButtons: bool,
  canReserve: bool,
  canPlaceOrder: bool,
  canPlaceStockReservationOrder: bool,
  orderType: string,
  loading: bool,
  defaultCurrency: string,
  totals: object,
  onReserve: func,
  onSubmit: func,
  displayVat: bool,
  selectedDSCountry: number,
  hasSupplierProducts: bool,
  productsType: string,
};
OrderSummary.displayName = 'OrderSummaryComponent';
export default OrderSummary;
